import { useBlocker } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import type { UnsavedChangesDialogProps } from "./UnsavedChangesDialog";

/**
 * Holds route changes behind the standard unsaved-changes dialog while there
 * are unsaved changes. Pass `onSave` only when the screen can save; it
 * resolves true when the save succeeded and the leave may continue.
 */
export function useUnsavedChangesGuard({
  hasUnsavedChanges,
  onSave,
}: {
  hasUnsavedChanges: boolean;
  onSave?: () => Promise<boolean>;
}): UnsavedChangesDialogProps & {
  requestLeave: (onLeave: () => void) => void;
  allowNavigation: () => void;
} {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Resolves true to block the held navigation and false to let it through.
  const pendingRef = useRef<((shouldBlock: boolean) => void) | null>(null);
  const allowNavigationRef = useRef(false);

  function shouldBlock() {
    if (!hasUnsavedChanges || allowNavigationRef.current) return false;
    return new Promise<boolean>((resolve) => {
      pendingRef.current?.(true);
      pendingRef.current = resolve;
      setOpen(true);
    });
  }

  useBlocker({
    shouldBlockFn: shouldBlock,
    enableBeforeUnload: () => hasUnsavedChanges && !allowNavigationRef.current,
    disabled: !hasUnsavedChanges,
  });

  // The screen's own save can clear the unsaved state while a leave is held;
  // there is then nothing left to ask, so let the held navigation through.
  // biome-ignore lint/correctness/useExhaustiveDependencies: settle only reads refs and state setters.
  useEffect(() => {
    if (!hasUnsavedChanges && !saving) {
      allowNavigationRef.current = false;
      settle(false);
    }
  }, [hasUnsavedChanges, saving]);

  // A held navigation must never outlive the screen.
  useEffect(() => () => pendingRef.current?.(true), []);

  function settle(shouldBlock: boolean) {
    const resolve = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    resolve?.(shouldBlock);
  }

  return {
    requestLeave: (onLeave) => {
      const blocked = shouldBlock();
      if (blocked === false) {
        onLeave();
        return;
      }
      void blocked.then((shouldStay) => {
        if (!shouldStay) onLeave();
      });
    },
    // Successful submissions may navigate synchronously before React renders
    // the saved state. Release that navigation without discarding a failed save.
    allowNavigation: () => {
      allowNavigationRef.current = true;
      settle(false);
    },
    open,
    onKeepEditing: () => settle(true),
    onDiscard: () => settle(false),
    onSave: onSave
      ? () => {
          if (saving) return;
          setSaving(true);
          void onSave()
            .then(
              (saved) => settle(!saved),
              () => settle(true),
            )
            .finally(() => setSaving(false));
        }
      : undefined,
    saving,
  };
}
